import type { z } from 'zod'
import type { EventKey } from '../../types/event-keys'
import { getEventKeyDefinition } from '../../registry'

export interface ValidateMetadataOptions {
  /** The event key to validate metadata against. */
  key: EventKey
  /** The metadata value to validate. */
  metadata: unknown
}

/**
 * Result of a metadata validation check.
 */
export interface ValidateMetadataResult {
  /** Whether the metadata passed validation (or no schema was defined). */
  success: boolean
  /** Zod validation error, present only when `success` is `false`. */
  error?: z.ZodError
}

/**
 * Validate metadata against the SDK registry Zod schema for a given event key.
 *
 * Returns `{ success: true }` when the event key has no registered schema,
 * metadata is `null`/`undefined`, or the metadata passes the schema.
 *
 * Returns `{ success: false, error }` when validation fails, with the full
 * `ZodError` attached for inspection.
 *
 * @param options - The event key and metadata to validate.
 * @returns A {@link ValidateMetadataResult}.
 *
 * @example
 * ```typescript
 * const result = validateMetadata({
 *   key: 'move.dispatched',
 *   metadata: { new: { status: 'dispatched' }, old: { status: 'planned' } },
 * })
 *
 * if (!result.success) {
 *   console.error(result.error?.issues)
 * }
 * ```
 */
export function validateMetadata(options: ValidateMetadataOptions): ValidateMetadataResult {
  const def = getEventKeyDefinition(options.key)

  if (!def?.metadataSchema || options.metadata === undefined || options.metadata === null) {
    return { success: true }
  }

  const result = def.metadataSchema.safeParse(options.metadata)
  if (result.success) {
    return { success: true }
  }

  return { success: false, error: result.error }
}
